import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { Form, Button, Row, Col, Spinner, Badge, Alert } from 'react-bootstrap';
import { useSnackbar } from 'notistack';
import { ProductContext } from '../../ProductProvider';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { validateKeywords, parseKeywords, MAX_KEYWORDS } from '../../utils/keywordValidator';
import './ArticlesForm.css';

const config = require('../../config.json');

export default function ArticlesForm(props) {
  const { enqueueSnackbar } = useSnackbar();
  const { journalData, loggedUser, articleData, setArticleData } = useContext(ProductContext);
  const { viewOnly = false, formType = 'New', handleClose, existingArticles = [], onSuccess, oldArticle } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingId, setIsGeneratingId] = useState(false);
  const [generatedSeq, setGeneratedSeq] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  //const [oldArticle, setOldArticle] = useState({});
  const isViewMode = formType === 'View' || viewOnly;
  const isEditMode = formType === 'Edit';
  const isNewMode = formType === 'New' && !viewOnly;

  // Active Issue & Journal identifiers
  const activeJournalID = articleData?.journalID || journalData?.journalID || '';
  const activeVolissuesNo = articleData?.volissuesNo || journalData?.volissuesNo || '';

  // Extract sequence number from article ID string given the volume/issue prefix
  const extractSequenceNumber = useCallback((idStr, targetVol) => {
    if (!idStr) return null;
    const str = String(idStr).trim();

    // 1. Match exact `${targetVol}-<num>` or `${targetVol}_<num>`
    if (targetVol) {
      const escapedVol = targetVol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const prefixRegex = new RegExp(`^${escapedVol}[-_](\\d+)$`, 'i');
      const matchPrefix = str.match(prefixRegex);
      if (matchPrefix && matchPrefix[1]) {
        return parseInt(matchPrefix[1], 10);
      }
    }

    // 2. Match general trailing suffix `...-<num>` or `..._<num>`
    const suffixMatch = str.match(/[-_](\d+)$/);
    if (suffixMatch && suffixMatch[1]) {
      return parseInt(suffixMatch[1], 10);
    }

    // 3. Match pure integer string `"1"`, `"2"`, etc.
    if (/^\d+$/.test(str)) {
      return parseInt(str, 10);
    }

    return null;
  }, []);

  // Auto-generate next sequential articleID for New formType
  const generateSequentialArticleID = useCallback(async (volissuesNo, journalID) => {
    if (!volissuesNo && !journalID) return;

    setIsGeneratingId(true);
    try {
      let articlesList = Array.isArray(existingArticles) && existingArticles.length > 0
        ? [...existingArticles]
        : [];

      // Fetch existing articles from backend API to ensure accurate sequential ID
      try {
        const { idToken } = (await fetchAuthSession()).tokens ?? {};
        const clientID = loggedUser?.website || 'VJCollege';
        const searchTopic = volissuesNo ? 'VolissuesNo' : 'JournalID';
        const searchValue = volissuesNo || journalID;

        const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/journal/articles`, {
          params: {
            fetchKey: searchValue,
            fetchOption: searchTopic,
            clientID: clientID
          },
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        });

        if (Array.isArray(response.data)) {
          // Merge unique articles
          const fetched = response.data;
          const map = new Map();
          articlesList.forEach(a => map.set(a.articleID || a.volissuesNoarticleID, a));
          fetched.forEach(a => map.set(a.articleID || a.volissuesNoarticleID, a));
          articlesList = Array.from(map.values());
        }
      } catch (fetchErr) {
        console.warn('Could not fetch existing articles for sequence calculation, using local list:', fetchErr);
      }

      // Filter articles belonging to this volume/issue
      const relevantArticles = volissuesNo
        ? articlesList.filter(a => !a.volissuesNo || String(a.volissuesNo).trim().toUpperCase() === String(volissuesNo).trim().toUpperCase())
        : articlesList;

      // Extract all sequence numbers
      const sequences = relevantArticles
        .map(a => extractSequenceNumber(a.articleID || a.volissuesNoarticleID, volissuesNo))
        .filter(n => n !== null && !isNaN(n) && n > 0);

      const maxSeq = sequences.length > 0 ? Math.max(...sequences) : 0;
      const nextSeq = maxSeq + 1;
      const newArticleID = volissuesNo ? `${volissuesNo}-${nextSeq}` : `ART-${nextSeq}`;

      setGeneratedSeq(nextSeq);
      setArticleData(prev => ({
        ...prev,
        journalID: prev.journalID || journalID || '',
        volissuesNo: prev.volissuesNo || volissuesNo || '',
        articleID: newArticleID,
        volissuesNoarticleID: newArticleID
      }));
    } catch (err) {
      console.error('Error generating sequential article ID:', err);
      // Fallback
      const fallbackID = volissuesNo ? `${volissuesNo}-1` : 'ART-1';
      setGeneratedSeq(1);
      setArticleData(prev => ({
        ...prev,
        articleID: fallbackID,
        volissuesNoarticleID: fallbackID
      }));
    } finally {
      setIsGeneratingId(false);
    }
  }, [existingArticles, extractSequenceNumber, loggedUser, setArticleData]);

  // Run auto-generation when entering "New" mode
  useEffect(() => {
    if (isNewMode) {
      const vol = articleData?.volissuesNo || journalData?.volissuesNo || '';
      const jId = articleData?.journalID || journalData?.journalID || '';

      // If articleID is empty or needs auto-generating
      generateSequentialArticleID(vol, jId);
    } else {
      // For Edit or View, make sure journalID and volissuesNo are populated
      setArticleData(prev => ({
        ...prev,
        journalID: prev.journalID || journalData?.journalID || '',
        volissuesNo: prev.volissuesNo || journalData?.volissuesNo || ''
      }));
    }
  }, [isNewMode, formType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live Page Range Calculation
  const pageStats = useMemo(() => {
    const start = parseInt(articleData?.startPageNo, 10);
    const end = parseInt(articleData?.endPageNo, 10);

    if (!isNaN(start) && !isNaN(end)) {
      if (end >= start) {
        return { valid: true, count: end - start + 1, message: `${end - start + 1} Pages (pp. ${start}–${end})` };
      } else {
        return { valid: false, count: 0, message: 'End page must be greater than or equal to start page' };
      }
    }
    return null;
  }, [articleData?.startPageNo, articleData?.endPageNo]);

  // Live Keyword Tags List
  const keywordTags = useMemo(() => {
    return parseKeywords(articleData?.articleKeywords);
  }, [articleData?.articleKeywords]);

  // Validation
  const validateForm = () => {
    const errors = {};
    if (!articleData?.articleID || articleData.articleID.trim() === '') {
      errors.articleID = 'Article ID is required (auto-generated).';
    }
    if (!articleData?.articleTitle || articleData.articleTitle.trim() === '') {
      errors.articleTitle = 'Article Title is required.';
    }
    if (!articleData?.articleAuthor || articleData.articleAuthor.trim() === '') {
      errors.articleAuthor = 'Article Author is required.';
    }
    const kwValidation = validateKeywords(articleData?.articleKeywords, true);
    if (!kwValidation.valid) {
      errors.articleKeywords = kwValidation.error;
    }
    if (!articleData?.startPageNo || String(articleData.startPageNo).trim() === '') {
      errors.startPageNo = 'Start Page is required.';
    } else if (isNaN(parseInt(articleData.startPageNo, 10)) || parseInt(articleData.startPageNo, 10) < 1) {
      errors.startPageNo = 'Must be a valid positive number.';
    }
    if (!articleData?.endPageNo || String(articleData.endPageNo).trim() === '') {
      errors.endPageNo = 'End Page is required.';
    } else if (isNaN(parseInt(articleData.endPageNo, 10)) || parseInt(articleData.endPageNo, 10) < 1) {
      errors.endPageNo = 'Must be a valid positive number.';
    }

    if (!errors.startPageNo && !errors.endPageNo) {
      const s = parseInt(articleData.startPageNo, 10);
      const e = parseInt(articleData.endPageNo, 10);
      if (e < s) {
        errors.endPageNo = 'End page cannot be less than start page.';
      }
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstErrMsg = Object.values(errors)[0];
      enqueueSnackbar(`Validation Error: ${firstErrMsg}`, { variant: 'error' });
      return false;
    }
    return true;
  };

  // Submit Handler (New Article - POST)
  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const { idToken } = (await fetchAuthSession()).tokens ?? {};

      const volissuesNo = articleData.volissuesNo || activeVolissuesNo;
      const articleID = articleData.articleID;
      const volissuesNoarticleID = articleData.volissuesNoarticleID || articleID;

      const payload = {
        journalID: articleData.journalID || activeJournalID,
        volissuesNoarticleID: volissuesNoarticleID,
        articleID: articleID,
        volissuesNo: volissuesNo,
        journalTitle: articleData.journalTitle || journalData?.journalTitle || journalData?.title || '',
        articleTitle: (articleData.articleTitle || '').trim().toUpperCase(),
        articleSubTitle: (articleData.articleSubTitle || '').trim().toUpperCase(),
        articleAuthor: (articleData.articleAuthor || '').trim().toUpperCase(),
        articleKeywords: (articleData.articleKeywords || '').trim().toUpperCase(),
        articleDescription: (articleData.articleDescription || '').trim().toUpperCase(),
        startPageNo: String(articleData.startPageNo || '').trim(),
        endPageNo: String(articleData.endPageNo || '').trim(),
        callNo: String(articleData.callNo || '').trim(),
        internalVolIssueNo: String(articleData.internalVolIssueNo || '').trim(),
        clientID: loggedUser?.website || 'VJCollege',
        updatedBy: loggedUser?.nickname || 'Librarian',
        updatedOn: new Date().toISOString()
      };

      await axios.post(`${config.api.invokeURL}/vjlibrary/journal/catalogue/articles`, payload, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      const keywordpayload = {
        article: payload,
        oldArticle: ""
      }
      //  console.log("keywordpayload", keywordpayload);

      await axios.post(`${config.api.invokeURL}/vjlibrary/journal/catalogue/articles/keyword`, keywordpayload, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      enqueueSnackbar(`Article "${payload.articleID}" created successfully!`, { variant: 'success' });
      if (onSuccess) onSuccess();
      if (handleClose) handleClose();
    } catch (error) {
      console.error('Error submitting article:', error);
      enqueueSnackbar(`Error: ${error.response?.data?.message || error.message}`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update Handler (Existing Article - PATCH)
  const handleUpdate = async (event) => {
    if (event) event.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const { idToken } = (await fetchAuthSession()).tokens ?? {};

      const payload = {
        journalID: articleData.journalID || activeJournalID,
        volissuesNoarticleID: articleData.volissuesNoarticleID || articleData.articleID,
        articleID: articleData.articleID,
        volissuesNo: articleData.volissuesNo || activeVolissuesNo,
        journalTitle: articleData.journalTitle || journalData?.journalTitle || journalData?.title || '',
        articleTitle: (articleData.articleTitle || '').trim().toUpperCase(),
        articleSubTitle: (articleData.articleSubTitle || '').trim().toUpperCase(),
        articleAuthor: (articleData.articleAuthor || '').trim().toUpperCase(),
        articleKeywords: (articleData.articleKeywords || '').trim().toUpperCase(),
        articleDescription: (articleData.articleDescription || '').trim().toUpperCase(),
        startPageNo: String(articleData.startPageNo || '').trim(),
        endPageNo: String(articleData.endPageNo || '').trim(),
        callNo: String(articleData.callNo || '').trim(),
        internalVolIssueNo: String(articleData.internalVolIssueNo || '').trim(),
        clientID: loggedUser?.website || 'VJCollege',
        updatedBy: loggedUser?.nickname || 'Librarian',
        updatedOn: new Date().toISOString()
      };

      await axios.patch(`${config.api.invokeURL}/vjlibrary/journal/catalogue/articles`, payload, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      if (oldArticle.articleKeywords !== articleData.articleKeywords) {
        const keywordpayload = {
          article: payload,
          oldArticle: oldArticle
        }
        //  console.log("keywordpayload", keywordpayload);

        await axios.post(`${config.api.invokeURL}/vjlibrary/journal/catalogue/articles/keyword`, keywordpayload, {
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        });

      }

      enqueueSnackbar(`Article "${payload.articleID}" updated successfully!`, { variant: 'success' });
      if (onSuccess) onSuccess();
      if (handleClose) handleClose();
    } catch (error) {
      console.error('Error updating article:', error);
      enqueueSnackbar(`Error: ${error.response?.data?.message || error.message}`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setArticleData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field if corrected
    if (formErrors[field]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  return (
    <div className="articles-form-container">
      {/* Context Summary Banner */}
      <div className="article-context-banner">
        <div>
          <div className="article-context-title">
            <i className={isViewMode ? 'fa fa-book-open' : isEditMode ? 'fa fa-edit' : 'fa fa-plus-circle'}></i>
            {isViewMode ? 'Article Overview' : isEditMode ? 'Edit Journal Article' : 'New Journal Article'}
          </div>
          <div className="article-context-subtitle">
            {journalData?.journalTitle ? journalData.journalTitle : 'Vidyajyoti Journal Repository'}
          </div>
        </div>
        <div className="article-context-meta">
          {activeJournalID && (
            <span className="context-meta-pill" title="Journal Identifier">
              <i className="fa fa-tag"></i> Journal: {activeJournalID}
            </span>
          )}
          {activeVolissuesNo && (
            <span className="context-meta-pill" title="Volume & Issue">
              <i className="fa fa-folder"></i> Issue: {activeVolissuesNo}
            </span>
          )}
          {articleData?.articleID && (
            <span className="context-meta-pill id-pill" title="Unique Article ID">
              <i className="fa fa-fingerprint"></i> ID: {articleData.articleID}
            </span>
          )}
        </div>
      </div>

      {/* VIEW ONLY READOUT */}
      {isViewMode ? (
        <div className="view-details-card">
          <div className="view-detail-row">
            <div className="view-detail-label"><i className="fa fa-key"></i> Article ID</div>
            <div className="view-detail-value">
              <span className="badge bg-primary px-3 py-2 fs-6">{articleData?.articleID || 'N/A'}</span>
            </div>
          </div>
          <div className="view-detail-row">
            <div className="view-detail-label"><i className="fa fa-heading"></i> Title</div>
            <div className="view-detail-value fw-bold text-dark">{articleData?.articleTitle || 'N/A'}</div>
          </div>
          {articleData?.articleSubTitle && (
            <div className="view-detail-row">
              <div className="view-detail-label"><i className="fa fa-subscript"></i> Subtitle</div>
              <div className="view-detail-value text-secondary">{articleData.articleSubTitle}</div>
            </div>
          )}
          <div className="view-detail-row">
            <div className="view-detail-label"><i className="fa fa-user-edit"></i> Author(s)</div>
            <div className="view-detail-value text-primary fw-semibold">{articleData?.articleAuthor || 'N/A'}</div>
          </div>
          <div className="view-detail-row">
            <div className="view-detail-label"><i className="fa fa-user-edit"></i> Vol / Issue No</div>
            <div className="view-detail-value text-primary fw-semibold">{articleData?.internalVolIssueNo || 'N/A'}</div>
          </div>
          <div className="view-detail-row">
            <div className="view-detail-label"><i className="fa fa-user-edit"></i> Call No</div>
            <div className="view-detail-value text-primary fw-semibold">{articleData?.callNo || 'N/A'}</div>
          </div>

          <div className="view-detail-row">
            <div className="view-detail-label"><i className="fa fa-file-alt"></i> Page Range</div>
            <div className="view-detail-value">
              <span className="badge bg-light text-dark border me-2">
                pp. {articleData?.startPageNo || '?'} – {articleData?.endPageNo || '?'}
              </span>
              {pageStats?.valid && (
                <span className="text-muted small">({pageStats.count} pages total)</span>
              )}
            </div>
          </div>
          <div className="view-detail-row">
            <div className="view-detail-label"><i className="fa fa-tags"></i> Keywords</div>
            <div className="view-detail-value">
              {keywordTags.length > 0 ? (
                <div className="keywords-preview-container">
                  {keywordTags.map((tag, idx) => (
                    <span key={idx} className="keyword-chip">
                      <i className="fa fa-hashtag text-primary me-1"></i>{tag}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-muted">None specified</span>
              )}
            </div>
          </div>
          <div className="view-detail-row">
            <div className="view-detail-label"><i className="fa fa-align-left"></i> Abstract</div>
            <div className="view-detail-value text-muted" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {articleData?.articleDescription || 'No abstract or description provided.'}
            </div>
          </div>

          <div className="article-form-actions">
            <Button variant="secondary" className="btn-article-cancel" onClick={handleClose}>
              <i className="fa fa-times me-1"></i> Close
            </Button>
          </div>
        </div>
      ) : (
        /* ADD / EDIT FORM */
        <Form onSubmit={isNewMode ? handleSubmit : handleUpdate}>
          {/* Section 1: Identifiers */}
          <div className="article-section-title">
            <i className="fa fa-id-card"></i> 1. Article Identification & Volume Mapping
          </div>
          <Row className="g-3 mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  Journal ID
                </Form.Label>
                <Form.Control
                  type="text"
                  value={articleData?.journalID || activeJournalID}
                  disabled
                  readOnly
                  placeholder="Journal ID"
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  Volume / Issue No
                </Form.Label>
                <Form.Control
                  type="text"
                  value={articleData?.volissuesNo || activeVolissuesNo}
                  disabled
                  readOnly
                  placeholder="Volume & Issue"
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="article-id-disabled-group">
                <Form.Label>
                  <span>
                    Article ID
                    {isNewMode && (
                      <span className="article-id-badge-tag ms-2">
                        <i className="fa fa-magic me-1"></i>Auto-Generated
                      </span>
                    )}
                    {isEditMode && (
                      <span className="article-id-badge-tag ms-2 bg-secondary text-white">
                        <i className="fa fa-lock me-1"></i>Locked
                      </span>
                    )}
                  </span>
                  {isGeneratingId && <Spinner animation="border" size="sm" className="text-primary" />}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={articleData?.articleID || ''}
                  disabled={true} // Article ID is locked across all modes
                  readOnly
                  className="article-id-disabled-input"
                  placeholder={isGeneratingId ? 'Generating sequential ID...' : 'e.g. Vol1-Issue1-1'}
                  isInvalid={!!formErrors.articleID}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.articleID}
                </Form.Control.Feedback>
                {isNewMode && generatedSeq && (
                  <div className="form-text text-muted" style={{ fontSize: '0.75rem' }}>
                    <i className="fa fa-check-circle text-success me-1"></i>
                    Sequence #{generatedSeq} for issue &quot;{activeVolissuesNo}&quot;
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Section 2: Article Title & Authors */}
          <div className="article-section-title mt-4">
            <i className="fa fa-book"></i> 2. Article Details & Contributors
          </div>
          <Row className="g-3 mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Article Title <span className="required-star">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={articleData?.articleTitle || ''}
                  onChange={(e) => handleFieldChange('articleTitle', e.target.value.toUpperCase())}
                  placeholder="Enter main title of the article"
                  isInvalid={!!formErrors.articleTitle}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.articleTitle}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Article Subtitle</Form.Label>
                <Form.Control
                  type="text"
                  value={articleData?.articleSubTitle || ''}
                  onChange={(e) => handleFieldChange('articleSubTitle', e.target.value.toUpperCase())}
                  placeholder="Enter subtitle (optional)"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Article Author(s) <span className="required-star">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={articleData?.articleAuthor || ''}
                  onChange={(e) => handleFieldChange('articleAuthor', e.target.value.toUpperCase())}
                  placeholder="e.g. Dr. John Doe, Prof. Jane Smith"
                  isInvalid={!!formErrors.articleAuthor}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.articleAuthor}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* Section 3: Indexing & Pagination */}
          <div className="article-section-title mt-4">
            <i className="fa fa-file-invoice"></i> 3. Pagination & Subject Keywords
          </div>
          <Row className="g-3 mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="d-flex justify-content-between align-items-center">
                  <span>Vol / Issue No <span className="required-star"></span></span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={articleData?.internalVolIssueNo || ''}
                  onChange={(e) => handleFieldChange('internalVolIssueNo', e.target.value.toUpperCase())}
                  placeholder="Vol / Issue No"
                  isInvalid={!!formErrors.internalVolIssueNo}
                />
                {/*} <Form.Control.Feedback type="invalid">
                  {formErrors.internalVolIssueNo}
                </Form.Control.Feedback> */}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="d-flex justify-content-between align-items-center">
                  <span>Call No<span className="required-star">*</span></span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={articleData?.callNo || ''}
                  onChange={(e) => handleFieldChange('callNo', e.target.value.toUpperCase())}
                  placeholder=""
                  isInvalid={!!formErrors.callNo}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.callNo}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={3} sm={6}>
              <Form.Group>
                <Form.Label>
                  Start Page No <span className="required-star">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={articleData?.startPageNo || ''}
                  onChange={(e) => handleFieldChange('startPageNo', e.target.value)}
                  placeholder="e.g. 1"
                  isInvalid={!!formErrors.startPageNo}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.startPageNo}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={3} sm={6}>
              <Form.Group>
                <Form.Label>
                  End Page No <span className="required-star">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={articleData?.endPageNo || ''}
                  onChange={(e) => handleFieldChange('endPageNo', e.target.value)}
                  placeholder="e.g. 15"
                  isInvalid={!!formErrors.endPageNo}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.endPageNo}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="d-flex justify-content-between align-items-center">
                  <span>Keywords / Tags <span className="required-star">*</span></span>
                  <span className={`badge ${keywordTags.length > MAX_KEYWORDS ? 'bg-danger text-white' : keywordTags.length >= 8 ? 'bg-warning text-dark' : 'bg-primary-subtle text-primary border'}`}>
                    {keywordTags.length} / {MAX_KEYWORDS} Max
                  </span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={articleData?.articleKeywords || ''}
                  onChange={(e) => handleFieldChange('articleKeywords', e.target.value.toUpperCase())}
                  placeholder="e.g. THEOLOGY, ECCLESIOLOGY, SCRIPTURE (comma-separated)"
                  isInvalid={!!formErrors.articleKeywords}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.articleKeywords}
                </Form.Control.Feedback>
                <div className="form-text text-muted" style={{ fontSize: '0.76rem' }}>
                  <i className="fa fa-info-circle me-1 text-primary"></i>
                  Enter up to {MAX_KEYWORDS} concise terms. Place paragraphs in <strong>Abstract / Summary</strong> below.
                </div>
              </Form.Group>
            </Col>

            {/* Live calculation tag & keywords preview */}
            <Col md={12} className="pt-1">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                {pageStats ? (
                  <div className={`page-calc-widget ${pageStats.valid ? '' : 'invalid-pages'}`}>
                    <i className={pageStats.valid ? 'fa fa-check-circle' : 'fa fa-exclamation-triangle'}></i>
                    {pageStats.message}
                  </div>
                ) : (
                  <span className="text-muted small">
                    <i className="fa fa-info-circle me-1"></i> Enter start and end page numbers to compute page length.
                  </span>
                )}

                {keywordTags.length > 0 && (
                  <div className="keywords-preview-container d-flex flex-wrap gap-1 align-items-center">
                    {keywordTags.map((tag, idx) => {
                      const isTooLong = tag.length > 40 || tag.split(/\s+/).length > 5;
                      return (
                        <span
                          key={idx}
                          className={`keyword-chip badge ${isTooLong ? 'bg-danger text-white' : 'bg-light text-dark border'}`}
                          style={{ fontSize: '0.78rem' }}
                        >
                          <i className={`fa ${isTooLong ? 'fa-exclamation-triangle me-1' : 'fa-hashtag text-primary me-1'}`}></i>
                          {tag}
                          {isTooLong && <span className="ms-1 fw-bold">(Too long)</span>}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Section 4: Description / Abstract */}
          <div className="article-section-title mt-4">
            <i className="fa fa-align-left"></i> 4. Abstract / Description
          </div>
          <Row className="g-3 mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label className="d-flex justify-content-between align-items-center">
                  <span>Article Abstract / Summary</span>
                  <span className="text-muted small fw-normal">
                    {(articleData?.articleDescription || '').length} characters
                  </span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={articleData?.articleDescription || ''}
                  onChange={(e) => handleFieldChange('articleDescription', e.target.value)}
                  placeholder="Enter a brief summary, abstract, or notes about this article..."
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Form Actions */}
          <div className="article-form-actions">
            <Button
              variant="outline-secondary"
              className="btn-article-cancel"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <i className="fa fa-times me-1"></i> Cancel
            </Button>

            {isNewMode && (
              <Button
                variant="primary"
                type="submit"
                className="btn-article-submit"
                disabled={isSubmitting || isGeneratingId}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving Article...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save me-2"></i>
                    Save Article
                  </>
                )}
              </Button>
            )}

            {isEditMode && (
              <Button
                variant="primary"
                type="submit"
                className="btn-article-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Updating Article...
                  </>
                ) : (
                  <>
                    <i className="fa fa-check-circle me-2"></i>
                    Update Article
                  </>
                )}
              </Button>
            )}
          </div>
        </Form>
      )}
    </div>
  );
}
